import { createPortal } from "preact/compat";
import type { ComponentChildren } from "preact";
import { useId, useLayoutEffect, useRef, useState } from "preact/hooks";
import { MapPinPlus, Plus, Trash2, X } from "lucide-react";
import type { MapDocument, MapLocation, MapStand } from "../shared/types";
import {
  addBoothNavigationLocation,
  addMapPlace,
  defaultPlacePosition,
  newPlaceId,
  removeMapPlace,
} from "../shared/placeAuthoring";
import type { VenueMapLogos } from "../map/useVenueMapLogos";
import { LocationMapEditor } from "./LocationMapEditor";
import "./place-authoring.css";

interface AuthoringProps {
  document: MapDocument;
  canEdit: boolean;
  disabled?: boolean;
  logos?: VenueMapLogos;
  onChange: (document: MapDocument) => void;
  onSelect?: (id: string) => void;
}

function PlaceDialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ComponentChildren;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useRef(onClose);
  close.current = onClose;
  const titleId = useId();
  useLayoutEffect(() => {
    const element = dialog.current;
    if (!element) return;
    const page = globalThis.document;
    const opener = page.activeElement;
    const previousOverflow = page.body.style.overflow;
    page.body.style.overflow = "hidden";
    element.showModal();
    element.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    return () => {
      element.close();
      page.body.style.overflow = previousOverflow;
      queueMicrotask(() => {
        if (
          opener instanceof HTMLElement &&
          opener.isConnected &&
          !page.querySelector("dialog[open]")
        )
          opener.focus({ preventScroll: true });
      });
    };
  }, []);
  return createPortal(
    <dialog
      ref={dialog}
      className="em-place-dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        close.current();
      }}
      onKeyDown={(event) => {
        if (
          (event.metaKey || event.ctrlKey) &&
          event.key.toLowerCase() === "s"
        ) {
          event.preventDefault();
          event.stopPropagation();
        }
      }}
    >
      <header>
        <h2 id={titleId}>{title}</h2>
        <button
          type="button"
          className="em-icon-button"
          aria-label="Close dialog"
          onClick={onClose}
        >
          <X size={19} aria-hidden="true" />
        </button>
      </header>
      {children}
    </dialog>,
    globalThis.document.body,
  );
}

export function AddPlace({
  document,
  canEdit,
  disabled = false,
  logos,
  onChange,
  onSelect,
}: AuthoringProps) {
  const [step, setStep] = useState<"name" | "position" | null>(null);
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const opener = useRef<HTMLButtonElement>(null);
  const unavailable = !canEdit || disabled;
  const close = () => {
    setStep(null);
    queueMicrotask(() => opener.current?.focus({ preventScroll: true }));
  };
  const pendingLocation: MapLocation = {
    id: newPlaceId(document, name),
    label: name.trim(),
    ...defaultPlacePosition(document),
  };
  return (
    <div className="em-place-authoring">
      <button
        ref={opener}
        type="button"
        className="em-button em-button-secondary"
        disabled={unavailable}
        aria-haspopup="dialog"
        onClick={(event) => {
          event.currentTarget.focus({ preventScroll: true });
          setError("");
          setStep("name");
        }}
      >
        <Plus size={17} aria-hidden="true" /> Add place
      </button>
      {error && (
        <p role="alert" className="em-place-error">
          {error}
        </p>
      )}
      {step === "name" && !unavailable && (
        <PlaceDialog title="Add place" onClose={close}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (unavailable || !name.trim()) return;
              setStep("position");
            }}
          >
            <p>
              Add an entrance, meeting point or other useful place. Next, choose
              its position on the map.
            </p>
            <label>
              Place name
              <input
                data-autofocus
                required
                maxLength={200}
                value={name}
                placeholder="For example, Information desk"
                onInput={(event) => setName(event.currentTarget.value)}
              />
            </label>
            <footer>
              <button
                type="button"
                className="em-button em-button-secondary"
                onClick={close}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="em-button"
                disabled={!name.trim()}
              >
                Choose position
              </button>
            </footer>
          </form>
        </PlaceDialog>
      )}
      {step === "position" && !unavailable && (
        <LocationMapEditor
          document={document}
          location={pendingLocation}
          logos={logos}
          returnFocusTo={() => opener.current}
          onClose={close}
          onApply={(position) => {
            if (unavailable) return;
            try {
              const next = addMapPlace(document, name, position);
              onChange(next.document);
              onSelect?.(next.location.id);
              setName("");
              close();
            } catch (reason) {
              setError(
                reason instanceof Error
                  ? reason.message
                  : "This place could not be added.",
              );
              close();
            }
          }}
        />
      )}
    </div>
  );
}

export function RemovePlace({
  document,
  location,
  canEdit,
  disabled = false,
  onChange,
  onSelect,
}: AuthoringProps & { location: MapLocation }) {
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState("");
  const unavailable = !canEdit || disabled;
  return (
    <div className="em-place-remove">
      <button
        type="button"
        className="em-inline-button em-place-remove-button"
        disabled={unavailable}
        aria-haspopup="dialog"
        onClick={(event) => {
          event.currentTarget.focus({ preventScroll: true });
          setError("");
          setConfirming(true);
        }}
      >
        <Trash2 size={15} aria-hidden="true" /> Remove place
      </button>
      {error && (
        <p role="alert" className="em-place-error">
          {error}
        </p>
      )}
      {confirming && !unavailable && (
        <PlaceDialog title="Remove place?" onClose={() => setConfirming(false)}>
          <p>
            Remove <strong>{location.label}</strong> from this draft?
          </p>
          <p>
            Pins and routes that use this place will stop working after you
            publish. The currently published map stays unchanged.
          </p>
          <footer>
            <button
              type="button"
              data-autofocus
              className="em-button em-button-secondary"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="em-button em-place-remove-confirm"
              onClick={() => {
                if (unavailable) return;
                try {
                  onChange(removeMapPlace(document, location.id));
                  onSelect?.("");
                  setConfirming(false);
                } catch (reason) {
                  setError(
                    reason instanceof Error
                      ? reason.message
                      : "This place could not be removed.",
                  );
                  setConfirming(false);
                }
              }}
            >
              Remove from draft
            </button>
          </footer>
        </PlaceDialog>
      )}
    </div>
  );
}

export function AddBoothLocation({
  document,
  stand,
  canEdit,
  disabled = false,
  onChange,
  onSelect,
}: AuthoringProps & { stand: MapStand }) {
  const [error, setError] = useState("");
  const unavailable = !canEdit || disabled;
  return (
    <div className="em-field-stack">
      <p className="em-muted">
        Add a navigation point to make this booth available to search, pins and
        routes. Its position starts inside the booth and can be adjusted on the
        map.
      </p>
      <button
        type="button"
        className="em-button em-button-secondary"
        disabled={unavailable}
        onClick={() => {
          if (unavailable) return;
          try {
            const next = addBoothNavigationLocation(document, stand.id);
            onChange(next.document);
            onSelect?.(next.location.id);
          } catch (reason) {
            setError(
              reason instanceof Error
                ? reason.message
                : "This navigation point could not be added.",
            );
          }
        }}
      >
        <MapPinPlus size={17} aria-hidden="true" /> Add navigation point
      </button>
      {error && (
        <p role="alert" className="em-place-error">
          {error}
        </p>
      )}
    </div>
  );
}
